import { useState, useRef, useEffect } from "react";

const CONFIANZA_ALTA  = 80;
const CONFIANZA_MEDIA = 50;

function badge(confianza, productoSugerido) {
  if (!productoSugerido)
    return { color: "#C0392B", bg: "#FDECEC", label: "Sin sugerencia" };
  if (confianza >= CONFIANZA_ALTA)
    return { color: "#1D6F42", bg: "#EAF7F0", label: `${confianza}% confianza` };
  return { color: "#7D5A00", bg: "#FEF9EC", label: `${confianza}% confianza` };
}

// Searchable combobox: filtrable por texto, muestra dropdown al hacer foco
function SearchableSelect({ value, onChange, options, placeholder = "Buscar producto..." }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const displayValue = value || "";
  const filtered = options.filter((o) =>
    o.toLowerCase().includes(query.toLowerCase())
  );

  // Cerrar al hacer clic afuera
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectOption = (opt) => {
    onChange(opt);
    setOpen(false);
    setQuery("");
  };

  const clearOption = () => {
    onChange("");
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          border: displayValue ? "1px solid #1D9E75" : "1px solid #e0a030",
          borderRadius: 8,
          background: displayValue ? "#f4fbf8" : "#fffbf2",
          padding: "0 8px",
          cursor: "text",
          minHeight: 34,
        }}
        onClick={() => {
          setOpen(true);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
      >
        {open ? (
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={displayValue || placeholder}
            style={{
              flex: 1,
              border: "none",
              background: "transparent",
              fontSize: 12,
              outline: "none",
              color: "#222",
              padding: "7px 0",
            }}
          />
        ) : (
          <span
            style={{
              flex: 1,
              fontSize: 12,
              color: displayValue ? "#222" : "#888",
              padding: "7px 0",
              userSelect: "none",
            }}
          >
            {displayValue || "— Sin asignar —"}
          </span>
        )}
        {displayValue && !open && (
          <button
            onMouseDown={(e) => { e.stopPropagation(); clearOption(); }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#999",
              fontSize: 14,
              padding: "0 2px",
              lineHeight: 1,
            }}
            title="Quitar asignación"
          >
            ×
          </button>
        )}
        <span style={{ color: "#999", fontSize: 10, marginLeft: 4, userSelect: "none" }}>▾</span>
      </div>

      {open && (
        <div
          style={{
            position: "absolute",
            zIndex: 100,
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            background: "#fff",
            border: "1px solid #ddd",
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            maxHeight: 200,
            overflowY: "auto",
          }}
        >
          <div
            onMouseDown={() => clearOption()}
            style={{
              padding: "8px 12px",
              fontSize: 12,
              color: "#888",
              cursor: "pointer",
              borderBottom: "1px solid #f0f0ee",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f3")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            — Sin asignar —
          </div>
          {filtered.length === 0 ? (
            <div style={{ padding: "8px 12px", fontSize: 12, color: "#aaa" }}>
              Sin resultados
            </div>
          ) : (
            filtered.map((opt) => (
              <div
                key={opt}
                onMouseDown={() => selectOption(opt)}
                style={{
                  padding: "8px 12px",
                  fontSize: 12,
                  color: opt === displayValue ? "#1D9E75" : "#222",
                  fontWeight: opt === displayValue ? 600 : 400,
                  cursor: "pointer",
                  background: opt === displayValue ? "#f4fbf8" : "transparent",
                }}
                onMouseEnter={(e) => {
                  if (opt !== displayValue) e.currentTarget.style.background = "#f5f5f3";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = opt === displayValue ? "#f4fbf8" : "transparent";
                }}
              >
                {opt}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Modal de confirmación con aviso de responsabilidad
function ModalConfirmacion({ sinAsignar, total, onConfirmar, onCancelar }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          padding: "1.75rem",
          maxWidth: 440,
          width: "100%",
          boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
        }}
      >
        <div style={{ fontSize: 28, marginBottom: 10, textAlign: "center" }}>⚠️</div>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 10px", textAlign: "center" }}>
          Confirmá antes de continuar
        </h3>
        <p style={{ fontSize: 13, color: "#444", lineHeight: 1.6, margin: "0 0 14px" }}>
          Estás a punto de generar el panel con estas asignaciones. Zenviq usó inteligencia
          automática para <strong>sugerir</strong> la correspondencia entre campañas y
          productos, pero <strong>la asignación final es tu responsabilidad</strong>.
        </p>
        <p style={{ fontSize: 13, color: "#444", lineHeight: 1.6, margin: "0 0 14px" }}>
          Los cálculos de rentabilidad, BEROAS y márgenes se basan en este mapeo. Una
          asignación incorrecta producirá métricas incorrectas.
        </p>
        {sinAsignar > 0 && (
          <div
            style={{
              fontSize: 12,
              color: "#7D5A00",
              background: "#FEF9EC",
              border: "1px solid #f0c04088",
              borderRadius: 8,
              padding: "8px 12px",
              marginBottom: 14,
            }}
          >
            <strong>{sinAsignar}</strong> de {total} campaña{total > 1 ? "s" : ""} quedará{sinAsignar > 1 ? "n" : ""} sin asignar
            — su gasto publicitario no se incluirá en los cálculos.
          </div>
        )}
        <div style={{ display: "flex", gap: "0.75rem", marginTop: 4 }}>
          <button
            onClick={onCancelar}
            style={{
              flex: 1,
              padding: "11px",
              borderRadius: 10,
              border: "0.5px solid #ddd",
              background: "transparent",
              fontSize: 13,
              color: "#555",
              cursor: "pointer",
            }}
          >
            Revisar mapeo
          </button>
          <button
            onClick={onConfirmar}
            style={{
              flex: 2,
              padding: "11px",
              borderRadius: 10,
              border: "none",
              background: "#059669",
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Sí, generar panel →
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MapeoMeta({ productos, mapeos, onConfirmar, onVolver }) {
  const [asignaciones, setAsignaciones] = useState(() => {
    const init = {};
    mapeos.forEach(({ campana, producto_sugerido }) => {
      init[campana] = producto_sugerido || "";
    });
    return init;
  });
  const [mostrarModal, setMostrarModal] = useState(false);

  const cambiar = (campana, valor) =>
    setAsignaciones((prev) => ({ ...prev, [campana]: valor }));

  const handleConfirmarFinal = () => {
    const mapeoFinal = {};
    mapeos.forEach(({ campana }) => {
      mapeoFinal[campana] = asignaciones[campana] || null;
    });
    onConfirmar(mapeoFinal);
  };

  const sinAsignar = Object.values(asignaciones).filter((v) => !v).length;

  return (
    <>
      {mostrarModal && (
        <ModalConfirmacion
          sinAsignar={sinAsignar}
          total={mapeos.length}
          onConfirmar={handleConfirmarFinal}
          onCancelar={() => setMostrarModal(false)}
        />
      )}

      <div style={{ maxWidth: 700, margin: "0 auto", padding: "2rem 0" }}>
        {/* Encabezado */}
        <div style={{ marginBottom: "1.25rem" }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, margin: "0 0 8px 0" }}>
            Mapeo de Campañas Meta → Productos
          </h2>
          <p style={{ fontSize: 13, color: "#444", margin: "0 0 8px", lineHeight: 1.6 }}>
            Zenviq analizó los nombres de tus campañas y <strong>sugirió automáticamente</strong> el
            producto Dropi que probablemente corresponde a cada una. Revisá cada fila y
            corregí donde sea necesario antes de confirmar.
          </p>
          <div
            style={{
              fontSize: 12,
              color: "#555",
              background: "#F0FBF7",
              border: "1px solid #BBE5D4",
              borderRadius: 8,
              padding: "8px 12px",
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
            }}
          >
            <span style={{ fontSize: 16, lineHeight: 1 }}>💡</span>
            <span>
              Las campañas sin asignar no se incluirán en los cálculos de gasto publicitario.
              Podés buscar productos escribiendo directamente en el selector.
            </span>
          </div>
        </div>

        {/* Leyenda de confianza */}
        <div
          style={{
            background: "#fafafa",
            border: "0.5px solid #e8e8e6",
            borderRadius: 10,
            padding: "12px 14px",
            marginBottom: "1rem",
          }}
        >
          <p style={{ fontSize: 12, fontWeight: 600, color: "#333", margin: "0 0 8px" }}>
            ¿Qué significa el nivel de confianza?
          </p>
          <p style={{ fontSize: 12, color: "#555", margin: "0 0 8px", lineHeight: 1.5 }}>
            La confianza es un puntaje interno que usa Zenviq para medir qué tan segura está
            la sugerencia, basándose en cuántas palabras del nombre de la campaña coinciden
            con el nombre del producto.
          </p>
          <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
            {[
              {
                color: "#1D6F42", bg: "#EAF7F0",
                label: `Alta (≥${CONFIANZA_ALTA}%)`,
                desc: "Coincidencia muy probable — verificá igual",
              },
              {
                color: "#7D5A00", bg: "#FEF9EC",
                label: `Media (≥${CONFIANZA_MEDIA}%)`,
                desc: "Coincidencia parcial — revisar con atención",
              },
              {
                color: "#C0392B", bg: "#FDECEC",
                label: "Sin sugerencia",
                desc: "No encontró coincidencia — asignación manual requerida",
              },
            ].map((item) => (
              <div
                key={item.label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  flex: "1 1 180px",
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    padding: "2px 9px",
                    borderRadius: 20,
                    background: item.bg,
                    color: item.color,
                    border: `1px solid ${item.color}30`,
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.label}
                </span>
                <span style={{ fontSize: 11, color: "#666" }}>{item.desc}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Tabla de mapeo */}
        <div
          style={{
            border: "0.5px solid #e0e0de",
            borderRadius: 10,
            overflow: "hidden",
            marginBottom: "1.25rem",
          }}
        >
          {/* Encabezados */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              background: "#f5f5f3",
              padding: "10px 16px",
              borderBottom: "0.5px solid #e0e0de",
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>Campaña en Meta</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>Producto Dropi asignado</span>
          </div>

          {/* Filas */}
          {mapeos.map(({ campana, producto_sugerido, confianza }) => {
            const { color, bg, label } = badge(confianza, producto_sugerido);
            const valorActual = asignaciones[campana] || "";

            return (
              <div
                key={campana}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  alignItems: "center",
                  padding: "10px 16px",
                  borderBottom: "0.5px solid #f0f0ee",
                  background: "#fff",
                  gap: "12px",
                }}
              >
                {/* Nombre campaña + badge */}
                <div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: "#222",
                      marginBottom: 4,
                      wordBreak: "break-word",
                    }}
                  >
                    {campana}
                  </div>
                  <span
                    style={{
                      fontSize: 10,
                      padding: "2px 8px",
                      borderRadius: 20,
                      background: bg,
                      color,
                      border: `1px solid ${color}30`,
                    }}
                  >
                    {label}
                  </span>
                </div>

                {/* Searchable combobox */}
                <SearchableSelect
                  value={valorActual}
                  onChange={(val) => cambiar(campana, val)}
                  options={productos}
                />
              </div>
            );
          })}
        </div>

        {/* Botones */}
        <div style={{ display: "flex", gap: "0.75rem" }}>
          <button
            onClick={onVolver}
            style={{
              flex: 1,
              padding: "11px",
              borderRadius: 10,
              border: "0.5px solid #ddd",
              background: "transparent",
              fontSize: 13,
              color: "#555",
              cursor: "pointer",
            }}
          >
            ← Volver
          </button>
          <button
            onClick={() => setMostrarModal(true)}
            style={{
              flex: 2,
              padding: "11px",
              borderRadius: 10,
              border: "none",
              background: "#111",
              color: "#fff",
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Confirmar y Generar Panel →
          </button>
        </div>
      </div>
    </>
  );
}
